import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
} from '@/components/ui'
import { calcularDna, fetchDnaCorporativo } from './api'

function num(v: unknown) {
  if (v == null || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

export function DnaPage() {
  const { canEdit } = useAuth()
  const qc = useQueryClient()
  const [msg, setMsg] = useState('')

  const dna = useQuery({
    queryKey: ['inteligencia', 'dna'],
    queryFn: fetchDnaCorporativo,
  })

  const mut = useMutation({
    mutationFn: () => calcularDna(),
    onSuccess: async () => {
      setMsg('DNA recalculado para o mês corrente.')
      await qc.invalidateQueries({ queryKey: ['inteligencia', 'dna'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao calcular DNA.')
    },
  })

  const itens = dna.data || []
  const atual = itens[0]

  return (
    <section className="hi-page">
      <PageHeader
        title="Perfil DNA"
        subtitle="DNA corporativo via API de inteligência. Perfil individual e skills do colaborador no legado."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/perfil-dna')}>
              Legado
            </a>
            {canEdit('perfil-dna') ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={mut.isPending}
                onClick={() => mut.mutate()}
              >
                Recalcular
              </button>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      {dna.isLoading ? <Muted>Carregando…</Muted> : null}
      {dna.isError ? (
        <Alert tone="danger">
          {dna.error instanceof ApiError ? dna.error.message : 'Falha ao carregar DNA.'}
        </Alert>
      ) : null}

      {atual ? (
        <KpiGrid>
          <Kpi label="Referência" value={String(atual.referencia || '—')} />
          <Kpi label="Score DNA" value={num(atual.score_dna ?? atual.score_geral)} />
          <Kpi label="Cultura" value={num(atual.cultura_score)} />
          <Kpi label="Liderança" value={num(atual.lideranca_score)} />
          <Kpi label="Aprendizado" value={num(atual.aprendizado_score)} />
          <Kpi label="Bem-estar" value={num(atual.bem_estar_score)} />
        </KpiGrid>
      ) : null}

      {atual?.narrativa_ia ? (
        <Section title="Narrativa">
          <p>{String(atual.narrativa_ia)}</p>
        </Section>
      ) : null}

      <Section title="Histórico" flush>
        {!dna.isLoading && itens.length === 0 ? <Empty>Nenhum snapshot de DNA.</Empty> : null}
        {itens.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Ref.</th>
                <th>Score</th>
                <th>Cultura</th>
                <th>Liderança</th>
                <th>Aprendizado</th>
                <th>Inovação</th>
                <th>Bem-estar</th>
                <th>Resultados</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((d) => (
                <tr key={String(d.id || d.referencia)}>
                  <td>{String(d.referencia || '—')}</td>
                  <td>
                    <strong>{num(d.score_dna ?? d.score_geral)}</strong>
                  </td>
                  <td>{num(d.cultura_score)}</td>
                  <td>{num(d.lideranca_score)}</td>
                  <td>{num(d.aprendizado_score)}</td>
                  <td>{num(d.inovacao_score)}</td>
                  <td>{num(d.bem_estar_score)}</td>
                  <td>{num(d.resultados_score)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}

export default DnaPage
